import { useLocalStorage } from '@shared/hooks/useLocalStorage';
import { createContext, useContext, useMemo } from 'react';
import { ColorName, COLORS } from '@shared/theme/themeSettings/config';
import { MuiThemeOverride } from '@shared/theme';

export interface ColorOption {
  name: ColorName;
  value: string;
}

export interface AppThemeContextValue {
  themeMode: 'light' | 'dark';
  themeDirection: 'rtl' | 'ltr';
  themeColor: ColorName;
  themeStretch: boolean;
  colorOption: ColorOption[];
  switchMode: () => void;
  onChangeMode: (event: any) => void;
  onChangeDirection: (event: any) => void;
  onChangeColor: (event: any) => void;
  onToggleStretch: (event: any) => void;
}

const initialState: AppThemeContextValue = {
  themeMode: 'light',
  themeDirection: 'ltr',
  themeColor: 'default',
  themeStretch: false,
  switchMode: () => {},
  onChangeMode: (event: any) => {},
  onChangeDirection: (event: any) => {},
  onChangeColor: (event: any) => {},
  onToggleStretch: (event: any) => {},
  colorOption: []
};
const AppThemeContext = createContext<AppThemeContextValue>(initialState);

export interface Props {
  children: React.ReactNode;
}

const resetAppThemeSettings = () => {
  const rawFlag = localStorage.getItem('settings:isReset') ?? '';

  const isAlreadyReset = !rawFlag ? false : JSON.parse(rawFlag);
  if (!isAlreadyReset) {
    localStorage.setItem('settings', JSON.stringify(initialState));
    localStorage.setItem('settings:isReset', JSON.stringify(true));
  }
};

// TODO: удалить когда тема будет сброшена
resetAppThemeSettings();

export const AppThemeProvider: React.FC<Props> = ({ children }) => {
  const [settings, setSettings] = useLocalStorage('settings', {
    themeMode: initialState.themeMode,
    themeDirection: initialState.themeDirection,
    themeColor: initialState.themeColor,
    themeStretch: initialState.themeStretch
  });

  const onChangeMode = (event: any) => {
    setSettings({
      ...settings,
      themeMode: event.target.value
    });
  };

  const onChangeDirection = (event: any) => {
    setSettings({
      ...settings,
      themeDirection: event.target.value
    });
  };

  const onChangeColor = (event: any) => {
    setSettings({
      ...settings,
      themeColor: event.target.value
    });
  };

  const onToggleStretch = () => {
    setSettings({
      ...settings,
      themeStretch: !settings.themeStretch
    });
  };

  const switchMode = () => {
    const newMode = settings.themeMode === 'light' ? 'dark' : 'light';
    setSettings({
      ...settings,
      themeMode: newMode
    });
  };

  const value = useMemo(
    () => ({
      ...settings,
      onChangeMode,
      onChangeDirection,
      switchMode,
      onChangeColor,
      colorOption: COLORS.map((color) => ({
        name: color.name,
        value: color.main
      })),
      onToggleStretch
    }),
    [onChangeColor, onChangeDirection, onChangeMode, onToggleStretch, settings, switchMode]
  );

  return (
    <AppThemeContext.Provider value={value}>
      <MuiThemeOverride>{children}</MuiThemeOverride>
    </AppThemeContext.Provider>
  );
};

export { AppThemeContext };

export const useAppTheme = () => {
  const context = useContext(AppThemeContext);
  if (!context) {
    throw new Error('useAppTheme must be used within a SettingsContext');
  }
  return context;
};
